import type {ImageTileIndex} from "#utils/tiled-image/tiled-image-types"

type MipLevelData = {
    numTilesX: number
    numTilesY: number
}

export class TiledImageIndex {
    static fromDescriptor(descriptor: {
        imageWidth: number
        imageHeight: number
        tileWidth: number
        tileHeight: number
        tileMipLevels: number
        tileMediaType: string
    }): TiledImageIndex {
        return new TiledImageIndex(descriptor)
    }

    private constructor(descriptor: {
        imageWidth: number
        imageHeight: number
        tileWidth: number
        tileHeight: number
        tileMipLevels: number
        tileMediaType: string
    }) {
        this._suffix = TiledImageIndex.getSuffix(descriptor.tileMediaType)
        let width = descriptor.imageWidth
        let height = descriptor.imageHeight
        this._mipLevelData = []
        for (let level = 0; level < descriptor.tileMipLevels; level++) {
            this._mipLevelData.push({
                numTilesX: Math.ceil(width / descriptor.tileWidth),
                numTilesY: Math.ceil(height / descriptor.tileHeight),
            })
            width = Math.ceil(width / 2)
            height = Math.ceil(height / 2)
        }
    }

    get numMipLevels(): number {
        return this._mipLevelData.length
    }

    get numTilesX(): number {
        if (this._mipLevelData.length === 0) {
            throw new Error("No mip levels available")
        }
        return this._mipLevelData[0].numTilesX
    }

    get numTilesY(): number {
        if (this._mipLevelData.length === 0) {
            throw new Error("No mip levels available")
        }
        return this._mipLevelData[0].numTilesY
    }

    get maxMipLevel(): number {
        return this._mipLevelData.length - 1
    }

    isValidTileIndex(imageTileIndex: ImageTileIndex): boolean {
        const mipLevelData = this._mipLevelData[imageTileIndex.mipLevel]
        if (!mipLevelData) {
            return false
        }
        if (imageTileIndex.tileIndex.x < 0 || imageTileIndex.tileIndex.x >= mipLevelData.numTilesX) {
            return false
        }
        if (imageTileIndex.tileIndex.y < 0 || imageTileIndex.tileIndex.y >= mipLevelData.numTilesY) {
            return false
        }
        return true
    }

    getTileFilename(imageTileIndex: ImageTileIndex): string {
        const mipLevelData = this._mipLevelData[imageTileIndex.mipLevel]
        if (!mipLevelData) {
            throw new Error(`Invalid mipLevel: ${imageTileIndex.mipLevel}`)
        }
        if (imageTileIndex.tileIndex.x < 0 || imageTileIndex.tileIndex.x >= mipLevelData.numTilesX) {
            throw new Error(`Invalid tileIndex.x: ${imageTileIndex.tileIndex.x} for mipLevel: ${imageTileIndex.mipLevel}`)
        }
        if (imageTileIndex.tileIndex.y < 0 || imageTileIndex.tileIndex.y >= mipLevelData.numTilesY) {
            throw new Error(`Invalid tileIndex.y: ${imageTileIndex.tileIndex.y} for mipLevel: ${imageTileIndex.mipLevel}`)
        }
        return `mip${imageTileIndex.mipLevel}_x${imageTileIndex.tileIndex.x}_y${imageTileIndex.tileIndex.y}.${this._suffix}?alt=media`
    }

    private static getSuffix(tileMediaType: string): string {
        switch (tileMediaType) {
            case "image/png":
                return "png"
            case "image/jpg":
            case "image/jpeg":
                return "jpg"
            case "image/webp":
                return "webp"
            default:
                throw new Error(`Unsupported media type: ${tileMediaType}`)
        }
    }

    private _mipLevelData: MipLevelData[]
    private _suffix: string
}
